import React, { useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import {
  Building2, Users, Link2, RefreshCw, Copy, Check, Download,
  Loader2, AlertTriangle, Info
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LinkGeneratorTab = ({
  linkGenerator, setLinkGenerator,
  generateClientLink, copyToClipboard,
  isDarkMode
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = (text) => {
    copyToClipboard(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const inputClass = `w-full text-sm border rounded-md px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-demoraes-green/30 focus:border-demoraes-green transition-colors ${
    isDarkMode ? 'bg-zinc-800 border-zinc-700 text-zinc-200 placeholder-zinc-500' : 'bg-white border-zinc-300 text-zinc-700 placeholder-zinc-400'
  }`;

  const labelClass = `text-xs font-semibold uppercase tracking-wider mb-1.5 block ${isDarkMode ? 'text-zinc-500' : 'text-zinc-500'}`;

  return (
    <div className="space-y-4">
      <div className={`rounded-lg border p-6 ${isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'}`}>
        <div className="flex items-center gap-2 mb-5">
          <Link2 size={18} className="text-demoraes-green" />
          <h3 className={`text-base font-bold ${isDarkMode ? 'text-zinc-200' : 'text-zinc-800'}`}
            style={{ fontFamily: 'Manrope, sans-serif' }}>
            Gerador de Links para Clientes
          </h3>
        </div>

        <div className="space-y-4 max-w-xl">
          <div>
            <label className={labelClass}>
              <Building2 size={11} className="inline mr-1" /> Nome do Cliente
            </label>
            <input
              type="text"
              value={linkGenerator.clientName}
              onChange={(e) => setLinkGenerator(prev => ({ ...prev, clientName: e.target.value, generatedLink: '' }))}
              placeholder="Ex: Empresa ABC"
              className={inputClass}
              data-testid="link-client-name"
            />
            <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-600' : 'text-zinc-400'}`}>
              O nome será usado como identificador (tag) para filtrar respostas
            </p>
          </div>

          <div>
            <label className={labelClass}>
              <Users size={11} className="inline mr-1" /> Número de Funcionários
            </label>
            <input
              type="number"
              min="1"
              max="10000"
              value={linkGenerator.employeeCount}
              onChange={(e) => setLinkGenerator(prev => ({ ...prev, employeeCount: e.target.value, generatedLink: '' }))}
              placeholder="Ex: 50"
              className={inputClass}
              data-testid="link-employee-count"
            />
            <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-600' : 'text-zinc-400'}`}>
              O acesso será bloqueado quando este limite for atingido
            </p>
          </div>

          {/* Nova Rodada */}
          <div className={`p-3 rounded-md border ${
            isDarkMode ? 'bg-amber-950/20 border-amber-800/40' : 'bg-amber-50 border-amber-200'
          }`}>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={linkGenerator.newRound}
                onChange={(e) => setLinkGenerator(prev => ({ ...prev, newRound: e.target.checked, generatedLink: '' }))}
                className="rounded border-zinc-400 w-4 h-4 mt-0.5 text-demoraes-green focus:ring-demoraes-green"
                data-testid="new-round-checkbox"
              />
              <div>
                <span className={`text-sm font-semibold ${isDarkMode ? 'text-amber-200' : 'text-amber-800'}`}>
                  Iniciar Nova Rodada
                </span>
                <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-amber-300/60' : 'text-amber-700/80'}`}>
                  Reaplicar pesquisa no mesmo ano (novos colaboradores, recontagem)
                </p>
              </div>
            </label>
          </div>

          <button
            onClick={generateClientLink}
            disabled={!linkGenerator.clientName.trim() || !linkGenerator.employeeCount || linkGenerator.loading}
            className="bg-demoraes-green hover:bg-demoraes-green/90 text-white text-sm font-semibold py-2.5 px-5 rounded-md transition-all disabled:opacity-40"
            data-testid="generate-link-btn"
          >
            {linkGenerator.loading ? (
              <span className="flex items-center gap-2">
                <Loader2 size={14} className="animate-spin" /> Gerando...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Link2 size={14} /> Gerar Link
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Generated Link Result */}
      {linkGenerator.generatedLink && (
        <div className={`rounded-lg border p-6 ${isDarkMode ? 'bg-zinc-900 border-demoraes-green/30' : 'bg-white border-demoraes-green/30'}`}>
          <div className="flex items-center gap-2 mb-4">
            <Check size={18} className="text-demoraes-green" />
            <h4 className={`text-sm font-bold text-demoraes-green`}>Link Gerado com Sucesso</h4>
          </div>

          <div className={`grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4 text-xs ${isDarkMode ? 'text-zinc-400' : 'text-zinc-600'}`}>
            <div><strong className={isDarkMode ? 'text-zinc-300' : 'text-zinc-700'}>Cliente:</strong> {linkGenerator.clientName}</div>
            <div><strong className={isDarkMode ? 'text-zinc-300' : 'text-zinc-700'}>Limite:</strong> {linkGenerator.employeeCount}</div>
            {linkGenerator.roundLabel && (
              <div><strong className={isDarkMode ? 'text-zinc-300' : 'text-zinc-700'}>Período:</strong> {linkGenerator.roundLabel}</div>
            )}
          </div>

          {/* Link + Copy */}
          <div className="flex items-center gap-2 mb-5">
            <input
              type="text"
              value={linkGenerator.generatedLink}
              readOnly
              className={`flex-1 text-sm border rounded-md px-3 py-2 ${
                isDarkMode ? 'bg-zinc-800 border-zinc-700 text-zinc-300' : 'bg-zinc-50 border-zinc-200 text-zinc-700'
              }`}
              data-testid="generated-link-input"
            />
            <button
              onClick={() => handleCopy(linkGenerator.generatedLink)}
              className={`p-2 rounded-md border transition-colors ${
                copied
                  ? 'bg-demoraes-green text-white border-demoraes-green'
                  : isDarkMode ? 'border-zinc-700 text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800' : 'border-zinc-200 text-zinc-600 hover:bg-zinc-50'
              }`}
              data-testid="copy-link-btn"
            >
              {copied ? <Check size={16} /> : <Copy size={16} />}
            </button>
          </div>

          {/* QR Code */}
          <div className={`rounded-md border p-5 text-center ${isDarkMode ? 'bg-zinc-800/50 border-zinc-700' : 'bg-zinc-50 border-zinc-200'}`}
            data-testid="qrcode-section">
            <p className={`text-xs font-semibold uppercase tracking-wider mb-3 ${isDarkMode ? 'text-zinc-500' : 'text-zinc-500'}`}>
              QR Code da Pesquisa
            </p>
            <div className="inline-block bg-white p-3 rounded-md" data-testid="qrcode-container">
              <QRCodeCanvas
                value={linkGenerator.generatedLink}
                size={180}
                level="H"
                marginSize={2}
                bgColor="#FFFFFF"
                fgColor="#000000"
              />
              <div style={{
                marginTop: '6px', fontSize: '10px', fontWeight: '700',
                color: '#00A74F', letterSpacing: '0.5px', textTransform: 'uppercase'
              }}>
                {linkGenerator.clientName.trim().split(/\s+/).slice(0, 2).join(' ')}
              </div>
            </div>
            <div className="mt-3">
              <button
                onClick={() => {
                  const canvas = document.querySelector('[data-testid="qrcode-container"] canvas');
                  if (!canvas) return;
                  const exportCanvas = document.createElement('canvas');
                  const padding = 24;
                  const labelHeight = 32;
                  exportCanvas.width = canvas.width + padding * 2;
                  exportCanvas.height = canvas.height + padding * 2 + labelHeight;
                  const ctx = exportCanvas.getContext('2d');
                  ctx.fillStyle = '#FFFFFF';
                  ctx.fillRect(0, 0, exportCanvas.width, exportCanvas.height);
                  ctx.drawImage(canvas, padding, padding);
                  const label = linkGenerator.clientName.trim().split(/\s+/).slice(0, 2).join(' ').toUpperCase();
                  ctx.fillStyle = '#00A74F';
                  ctx.font = 'bold 14px Calibri, Arial, sans-serif';
                  ctx.textAlign = 'center';
                  ctx.fillText(label, exportCanvas.width / 2, canvas.height + padding + labelHeight - 4);
                  const link = document.createElement('a');
                  link.download = `qrcode_${linkGenerator.clientName.replace(/\s+/g, '_').toLowerCase()}.png`;
                  link.href = exportCanvas.toDataURL('image/png');
                  link.click();
                }}
                className="text-xs bg-demoraes-green hover:bg-demoraes-green/90 text-white font-semibold py-2 px-4 rounded-md transition-all inline-flex items-center gap-1.5"
                data-testid="download-qrcode-btn"
              >
                <Download size={13} /> Baixar QR Code
              </button>
            </div>
          </div>

          {/* Warning */}
          <div className={`mt-4 flex items-start gap-2 p-3 rounded-md border text-xs ${
            isDarkMode ? 'bg-amber-950/20 border-amber-800/40 text-amber-300' : 'bg-amber-50 border-amber-200 text-amber-800'
          }`}>
            <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
            O acesso será bloqueado após {linkGenerator.employeeCount} respostas.
          </div>
        </div>
      )}
    </div>
  );
};
